import { Colors } from "../../../utils/colors";
import { useSelector, useDispatch } from "react-redux";
import { getAccountState } from "../../../redux/slices/accountSlice";
import { Icon } from "@iconify/react";
import EmptyStateButton from "../../Buttons/EmptyStateButton";
import StandardBorderedContainer from "../../Containers/StandardBorderedContainer";
import { HorizontalDivider } from "../../Dividers/HorizontalDivider";
import TeamMemberDetailsModal from "./TeamMemberDetailsModal";
import { useCallback, useMemo, useState } from "react";
import { AccountData, PlanTypeV4 } from "@markit/common.types";
import RectangleButton from "../../Buttons/RectangleButton";
import { CircularProgress } from "@mui/material";
import TeamMemberItem from "../../DisplayItem/TeamMemberItem";
import CreateTeamMemberModal from "./CreateTeamMemberModal";
import { useLoadUserTeamMembersList } from "../../../hooks/useLoadUserTeamMembersList";
import useAsyncOnMount from "../../../hooks/useAsyncEffectOnMount";
import {
  MARKIT_DOCS,
  hasSubscription,
  hasSubscriptionUnlockedAdvancedData,
  isBusinessSubscription,
} from "@markit/common.utils";
import FlatlistLoadMore from "../../FlatlistLoadMore";
import { notificationActions } from "../../../redux/slices/notificationSlice";
import EmptyStateButtonSubscription from "../../Buttons/EmptyState/EmptyStateButtonSubscription";

const PRO_PLAN_SEAT_LIMIT = 3;

const TeamMembersPanel = () => {
  const { accountData, teamMemberData } = useSelector(getAccountState).account;
  const dispatch = useDispatch();
  const [createMemberModal, setCreateMemberModal] = useState(false);
  const [selectedTeamMemberId, setSelectedTeamMemberId] = useState("");
  const [loadingUsers, setLoadingUsers] = useState(true);

  const {
    fetchedUserData,
    fetchedTeamMemberData,
    loadUsers,
    loadMoreUsers,
    isFinished,
    isLoading,
  } = useLoadUserTeamMembersList({
    userId: accountData.uid,
    windowSize: 10,
  });

  useAsyncOnMount(async () => {
    await loadUsers();
    setLoadingUsers(false);
  });

  const atSeatLimit = useMemo(
    () =>
      !isBusinessSubscription(accountData) &&
      fetchedTeamMemberData.length >= PRO_PLAN_SEAT_LIMIT - 1, // -1 because the primary admin user is included
    [accountData, fetchedTeamMemberData.length]
  );

  const onCreateMemberPress = useCallback(() => {
    if (atSeatLimit) {
      dispatch(
        notificationActions.setMarkitPlusModal(PlanTypeV4.BUSINESS.name)
      );
    } else {
      setCreateMemberModal(true);
    }
  }, [atSeatLimit, dispatch]);

  const foundSelectedTeamMember = useMemo(
    () =>
      fetchedTeamMemberData.find(
        (member) => member.uid === selectedTeamMemberId
      ),
    [fetchedTeamMemberData, selectedTeamMemberId]
  );

  const foundSelectedTeamMemberUserData = useMemo(
    () => fetchedUserData.find((user) => user.uid === selectedTeamMemberId),
    [fetchedUserData, selectedTeamMemberId]
  );

  const renderMemberItem = useCallback(
    (item: AccountData) => {
      const teamMember = fetchedTeamMemberData.find(
        (member) => member.uid === item.uid
      );
      const lastUserUid = fetchedUserData[fetchedUserData.length - 1]?.uid;
      return (
        <div key={item.uid}>
          <TeamMemberItem
            item={item}
            teamMember={teamMember}
            onItemPress={() => {
              if (!item || !teamMember) {
                alert(
                  "Could not view this team member at this time. Please try again later."
                );
                return;
              }
              setSelectedTeamMemberId(item.uid);
            }}
            disabled={teamMemberData !== undefined}
          />
          {(
            item.uid === accountData.uid
              ? fetchedUserData.length > 0
              : item.uid !== lastUserUid
          ) ? (
            <HorizontalDivider altMargin={14} />
          ) : null}
        </div>
      );
    },
    [accountData.uid, fetchedTeamMemberData, fetchedUserData, teamMemberData]
  );

  return (
    <div className="ColumnNormal" style={{ margin: "auto", gap: 24 }}>
      {!loadingUsers ? (
        <StandardBorderedContainer
          containerStyles={{
            width: 544,
            margin: "auto",
            backgroundColor: Colors.WHITE,
            paddingTop: 14,
          }}
        >
          <div className="AlignedRowSpaced" style={{ paddingInline: 14 }}>
            <div className="ColumnNormal" style={{ gap: 4 }}>
              <div className="AlignedRow" style={{ gap: 7 }}>
                <span className="sectionTitle">
                  {fetchedTeamMemberData.length > 0 ? "Manage" : "Invite"} Team
                  Members
                </span>
                <Icon
                  icon={"mdi:account-group"}
                  height={24}
                  style={{ color: Colors.BLACK }}
                />
              </div>
              <span className="smallBodySubtext">
                {teamMemberData
                  ? "View your team members"
                  : "Invite and manage team members and their roles"}
              </span>
            </div>
            {fetchedTeamMemberData.length > 0 &&
            !teamMemberData &&
            hasSubscriptionUnlockedAdvancedData(accountData) ? (
              <div>
                <RectangleButton
                  buttonLabel={atSeatLimit ? "Upgrade" : "Create New"}
                  onPress={onCreateMemberPress}
                  altColor={Colors.BLACK}
                  altTextColor={Colors.WHITE}
                  containerStyles={{ paddingInline: 14, paddingBlock: 12 }}
                />
              </div>
            ) : null}
          </div>
          <HorizontalDivider altMargin={14} />
          <div style={{ paddingInline: 14 }}>
            {renderMemberItem(accountData)}
            <FlatlistLoadMore
              fullList={fetchedUserData}
              currentList={fetchedUserData}
              loadMoreOnPress={loadMoreUsers}
              renderItem={(item) => renderMemberItem(item)}
              isLoadingMore={isLoading}
              showLoadMore={!isFinished}
              hideShowingAllResults={isFinished && fetchedUserData.length > 0}
              renderWhenEmpty={
                hasSubscriptionUnlockedAdvancedData(accountData) ? (
                  <EmptyStateButton
                    title={"Invite Team Members"}
                    description={
                      "Your team members and their permissions will show up here. Create new accounts and share them with people to join your team."
                    }
                    icon={
                      <Icon
                        icon={"mdi:account-group"}
                        height={39}
                        style={{ color: Colors.BLACK }}
                      />
                    }
                    iconBox={60}
                    iconBackgroundColor={Colors.WHITE}
                    btnText={"Create New"}
                    onPress={onCreateMemberPress}
                    containerStyles={{ paddingBlock: 24 }}
                  />
                ) : null
              }
              altFooter={
                isFinished && fetchedUserData.length > 0
                  ? isBusinessSubscription(accountData)
                    ? `Showing all ${fetchedUserData.length + 1} seats`
                    : `${fetchedTeamMemberData.length + 1}/${PRO_PLAN_SEAT_LIMIT} team member seats used`
                  : null
              }
            />
          </div>
          {!hasSubscriptionUnlockedAdvancedData(accountData) ? (
            <EmptyStateButtonSubscription
              planType={PlanTypeV4.PRO}
              iconName={"mdi:account-group"}
              btnText={
                hasSubscription(accountData) ? "Upgrade to Pro" : "Get Pro Plan"
              }
              containerStyles={{ paddingBlock: 30 }}
            />
          ) : null}
        </StandardBorderedContainer>
      ) : (
        <div className="Centering" style={{ minHeight: 400 }}>
          <CircularProgress style={{ color: Colors.GRAY1 }} size={24} />
        </div>
      )}
      <div style={{ margin: "auto" }}>
        <RectangleButton
          buttonLabel={"Learn More"}
          onPress={() => {
            window.open(MARKIT_DOCS + "/teams", "_blank");
          }}
          iconRight={
            <Icon
              icon={"ion:arrow-forward"}
              height={16}
              style={{
                color: Colors.BLACK,
                transform: "rotateY(0deg) rotate(-45deg)",
              }}
            />
          }
          altColor={Colors.WHITE}
          altTextColor={Colors.BLACK}
          containerStyles={{
            border: `0.5px solid ${Colors.GRAY2}`,
            paddingBlock: 14,
            paddingInline: 20,
            borderRadius: 100,
          }}
        />
      </div>
      {createMemberModal ? (
        <CreateTeamMemberModal
          isVisible={createMemberModal}
          setIsVisible={() => {
            setCreateMemberModal(false);
          }}
        />
      ) : null}
      {selectedTeamMemberId &&
      foundSelectedTeamMember &&
      foundSelectedTeamMemberUserData ? (
        <TeamMemberDetailsModal
          isVisible={selectedTeamMemberId !== ""}
          setIsVisible={() => setSelectedTeamMemberId("")}
          teamMember={foundSelectedTeamMember}
          item={foundSelectedTeamMemberUserData}
        />
      ) : null}
    </div>
  );
};

export default TeamMembersPanel;
